import { useState, useRef, useEffect, useCallback } from 'react'
import toast from 'react-hot-toast';
import { PageLoader } from '../../components/common/Loader';
import Breadcrumb from '../../components/common/Breadcrumb'
import { useAuth } from '../../contexts/AuthContext'
import { useTimesheetKind } from '../../hooks/useLaborTimesheets';
import LaborTimesheet from './LaborTimesheet';
import {
    IconPlus, IconDeviceFloppy, IconChevronLeft, IconChevronRight,
    IconTrash, IconClock, IconCalendarCheck, IconAlertCircle,
    IconSearch, IconCalendarMonth, IconBeach, IconX,
    IconMessage, IconLock, IconCheck, IconAlertTriangle, IconSignature,
    IconChevronDown
} from '@tabler/icons-react'
import ChargeLookupModal from './ChargeLookupModal'
import { publicHolidays, months, getAvailableYears } from '../../data/timesheetData'
import { useTimesheets, useMonthlyTimesheet, useCreateTimesheet, useUpdateTimesheet, useCreateTimesheetEntry, useUpdateTimesheetEntry, useDeleteTimesheetEntry, useSaveTimesheet, useSignTimesheet, useCreateTimesheetLine, useUpsertTimesheetEntries, useCreateHolidays, useMyApprovedLeaves, useDownloadStandardTimesheetReport, useHolidays } from '../../hooks/useTimesheets';
import { useProjects } from '../../hooks/useProjects';
import { useOrganizations } from '../../hooks/useOrganizations';
import { useMyProfile, useEmployees } from '../../hooks/useEmployees';
import { useGoogleHolidays } from '../../hooks/useGoogleHolidays';
import { isTimesheetHR } from '../../utils/reportPermissions';
import ReportDownloadButton from '../../components/common/ReportDownloadButton';

export default function Timesheet() {
    const { data: kindData, isLoading: isLoadingKind } = useTimesheetKind();
    const { user, hasApiPermission } = useAuth();
    const orgId = user?.orgId;
    const employeeId = user?.employeeId;
    const isReportHR = isTimesheetHR(user, hasApiPermission);
    const { data: reportEmployees } = useEmployees(orgId, isReportHR);
    const { data: myProfile } = useMyProfile();

    const currentDate = new Date();
    const [selectedMonth, setSelectedMonth] = useState(currentDate.getMonth());
    const [selectedYear, setSelectedYear] = useState(currentDate.getFullYear());
    // Use monthly endpoint which auto-creates timesheet
    const { data: monthlyData, isLoading: isLoadingTimesheet } = useMonthlyTimesheet(
        orgId,
        selectedYear,
        selectedMonth + 1, 
        employeeId
    );
    const { data: projectsData } = useProjects(orgId);
    const { data: organizationsData } = useOrganizations();
    // Fetch approved leaves for auto-population - pass employeeId to filter by logged-in user
    const { data: approvedLeavesData } = useMyApprovedLeaves(
        orgId,
        selectedYear,
        selectedMonth + 1,
        employeeId
    );
    // Fetch public holidays from Nager.Date 
    const { data: googleHolidaysData } = useGoogleHolidays(selectedYear);
    // Fetch HR-created custom holidays 
    const { data: backendHolidaysData } = useHolidays(orgId);
    // Merge: use Google holidays as base, add custom holidays
    const googleList = googleHolidaysData || [];
    const backendList = Array.isArray(backendHolidaysData)
        ? backendHolidaysData
        : backendHolidaysData?.results || [];
    const googleDates = new Set(googleList.map(h => h.date));
    const apiHolidays = [
        ...googleList,
        ...backendList.filter(h => !googleDates.has(h.date)).map(h => ({ id: h.id, name: h.name, date: h.date })),
    ];
    const projects = projectsData?.results || projectsData || [];
    const organizations = organizationsData?.results || organizationsData || [];
    const createTimesheet = useCreateTimesheet();
    const updateTimesheet = useUpdateTimesheet();
    const createEntry = useCreateTimesheetEntry();
    const updateEntry = useUpdateTimesheetEntry();
    const deleteEntry = useDeleteTimesheetEntry();
    const saveTimesheet = useSaveTimesheet();
    const signTimesheet = useSignTimesheet();
    const createLine = useCreateTimesheetLine();
    const upsertEntries = useUpsertTimesheetEntries();
    const createHolidays = useCreateHolidays();
    const downloadStandardReport = useDownloadStandardTimesheetReport();
    const [timeEntries, setTimeEntries] = useState([]);
    const [selectedIds, setSelectedIds] = useState([]);
    const [isLookupOpen, setIsLookupOpen] = useState(false);
    const [activeEntryId, setActiveEntryId] = useState(null);
    const [showMonthPicker, setShowMonthPicker] = useState(false);
    const [currentTimesheetId, setCurrentTimesheetId] = useState(null);
    const [approvedLeaves, setApprovedLeaves] = useState([]);
    const [dayIds, setDayIds] = useState({}); 

    useEffect(() => {
        if (!monthlyData) return;
        setCurrentTimesheetId(monthlyData.id);
        const capitalizedStatus = monthlyData.status
            ? monthlyData.status.charAt(0).toUpperCase() + monthlyData.status.slice(1)
            : 'Open';
        setTimesheetStatus(capitalizedStatus);
        setSupervisorComment(
            monthlyData.rejection_reason || monthlyData.reject_reason ||
            monthlyData.supervisor_comment || monthlyData.manager_comment || ''
        );
        if (monthlyData.signed_at || monthlyData.submitted_at) {
            setSubmittedAt(monthlyData.signed_at || monthlyData.submitted_at);
        }
        const projectEntries = [];
        if (monthlyData.lines && Array.isArray(monthlyData.lines)) {
            monthlyData.lines.forEach(line => {
                const entry = {
                    id: line.id, 
                    lineId: line.id, 
                    rowKey: `${line.project?.id}_${line.activity_code}`,
                    description: line.description || '',
                    project: line.project?.name || 'Unknown Project',
                    projectCode: line.project?.id,
                    plc: line.activity_code,
                    hours: {},
                    _entryIds: {},
                    isLeave: false,
                    isTimeOff: false
                };

                if (line.entries && typeof line.entries === 'object') {
                    Object.entries(line.entries).forEach(([dateStr, hoursValue]) => {
                        const dayOfMonth = parseInt(dateStr.split('-')[2], 10);
                        const dayIndex = dayOfMonth - 1; 
                        const hours = typeof hoursValue === 'object' ? hoursValue.hours : hoursValue;
                        if (hours > 0) {
                            entry.hours[dayIndex] = parseFloat(hours);
                            if (typeof hoursValue === 'object' && hoursValue.id) {
                                entry._entryIds[dayIndex] = hoursValue.id;
                            }
                        }
                    });
                }
                projectEntries.push(entry);
            });
        }
        // Preserve existing time-off entries, only update project entries
        setTimeEntries(prevEntries => {
            const existingTimeOffEntries = prevEntries.filter(e => e.isTimeOff);
            return [...projectEntries, ...existingTimeOffEntries];
        });
    }, [monthlyData]);
    useEffect(() => {
        if (approvedLeavesData) {
            const leaves = (approvedLeavesData.leaves || []).map(leave => ({
                id: leave.id,
                leaveTypeName: leave.leave_type,
                startDate: leave.start_date,
                endDate: leave.end_date,
                daysRequested: leave.days_requested,
                dates: leave.dates || []
            }));
            setApprovedLeaves(leaves);
        }
    }, [approvedLeavesData]);
    const [timesheetStatus, setTimesheetStatus] = useState('Open');
    const [signedAt, setSignedAt] = useState(null);
    const [submittedAt, setSubmittedAt] = useState(null);
    const [supervisorComment, setSupervisorComment] = useState('');
    const [commentModal, setCommentModal] = useState({ isOpen: false, entryId: null, dayIndex: null });
    const [comments, setComments] = useState({});
    const [showSignModal, setShowSignModal] = useState(false);
    const [tooltip, setTooltip] = useState({ visible: false, x: 0, y: 0, content: '' });
    const [leftScrollPct, setLeftScrollPct] = useState(0);
    const [rightScrollPct, setRightScrollPct] = useState(0);
    const leftPaneRef = useRef(null);
    const rightPaneRef = useRef(null);
    const isDragging = useRef(false);
    const dragTarget = useRef(null);
    const startX = useRef(0);
    const scrollStart = useRef(0);
    const dateInputRefs = useRef({});
    const monthPickerRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (monthPickerRef.current && !monthPickerRef.current.contains(e.target)) {
                setShowMonthPicker(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);
    // Generate days for selected month
    const getPeriodDays = useCallback(() => {
        const daysInMonth = new Date(selectedYear, selectedMonth + 1, 0).getDate();
        const days = [];
        for (let d = 1; d <= daysInMonth; d++) {
            days.push(new Date(selectedYear, selectedMonth, d));
        }
        return days;
    }, [selectedMonth, selectedYear]);
    const periodDays = getPeriodDays();
    const isWeekend = (date) => { const d = date.getDay(); return d === 0 || d === 6; };
    // Helper to format date as YYYY-MM-DD in local timezone (avoids UTC shift)
    const formatDateLocal = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };
    // Check if date is a public holiday 
    const getPublicHoliday = (date) => {
        const dateStr = formatDateLocal(date);
        return apiHolidays.find(h => h.date === dateStr);
    };
    // Check if date is an approved leave (excludes weekends)
    const getApprovedLeave = (date) => {
        if (isWeekend(date)) return null;
        const dateStr = formatDateLocal(date);
        return approvedLeaves.find(leave => {
            // Check if date is in the leave's dates 
            if (leave.dates && leave.dates.includes(dateStr)) {
                return true;
            }
            // Fallback: check date range
            const start = leave.fromDate || leave.startDate;
            const end = leave.toDate || leave.endDate;
            return dateStr >= start && dateStr <= end;
        });
    };
    // Find first non-weekend, non-holiday day index
    const getFirstWorkdayIndex = useCallback(() => {
        return periodDays.findIndex(date => !isWeekend(date) && !getPublicHoliday(date) && !getApprovedLeave(date));
    }, [periodDays, approvedLeaves]);
    // Calculate expected working hours for the month (excluding holidays and approved leave)
    const calculateExpectedHours = useCallback(() => {
        let workDays = 0;
        periodDays.forEach(date => {
            if (!isWeekend(date) && !getPublicHoliday(date) && !getApprovedLeave(date)) {
                workDays++;
            }
        });
        return workDays * 8;
    }, [periodDays, approvedLeaves]);
    // Calculate holiday/leave hours auto-filled
    const calculateAutoFilledHours = useCallback(() => {
        let hours = 0;
        periodDays.forEach(date => {
            const holiday = getPublicHoliday(date);
            const leave = getApprovedLeave(date);
            if ((holiday || leave) && !isWeekend(date)) {
                hours += 8;
            }
        });
        return hours;
    }, [periodDays, approvedLeaves]);
    const handleAddNewLine = () => {
        if (timesheetStatus !== 'Open' && timesheetStatus !== 'Saved' && timesheetStatus !== 'Rejected') return;
        const newEntry = {
            id: Date.now(),
            description: '',
            project: '',
            projectCode: '',
            plc: '',
            payType: '',
            isLeave: false,
            isTimeOff: false,
            leaveBalance: null,
            hours: {}
        };
        setTimeEntries([...timeEntries, newEntry]);
    };
    const handleDeleteSelected = () => {
        if (timesheetStatus !== 'Open' && timesheetStatus !== 'Saved' && timesheetStatus !== 'Rejected') return;
        setTimeEntries(timeEntries.filter(entry => !selectedIds.includes(entry.id)));
        setSelectedIds([]);
    };
    const handleCheckboxChange = (entryId) => {
        setSelectedIds(prev =>
            prev.includes(entryId)
                ? prev.filter(id => id !== entryId)
                : [...prev, entryId]
        );
    };
    const handleSelectAll = (e) => {
        if (e.target.checked) {
            setSelectedIds(timeEntries.map(entry => entry.id));
        } else {
            setSelectedIds([]);
        }
    };
    const openLookupForEntry = (entryId) => {
        if (timesheetStatus !== 'Open' && timesheetStatus !== 'Saved' && timesheetStatus !== 'Rejected') return;
        setActiveEntryId(entryId);
        setIsLookupOpen(true);
    };
    const handleLookupSelect = (selectionData) => {
        setTimeEntries(prev => prev.map(entry => {
            if (entry.id === selectionData.entryId) {
                const updated = {
                    ...entry,
                    description: selectionData.description,
                    project: selectionData.project.name,
                    projectCode: selectionData.projectCode,
                    plc: selectionData.plc,
                    payType: selectionData.payType,
                    isLeave: selectionData.isLeave,
                    isTimeOff: selectionData.isTimeOff || false,
                    timeOffType: selectionData.timeOffType || null,
                    leaveBalance: selectionData.leaveBalance,
                    chargeType: selectionData.chargeType
                };
                return updated;
            }
            return entry;
        }));
        setTimeout(() => {
            const firstWorkdayIdx = getFirstWorkdayIndex();
            const inputRef = dateInputRefs.current[`${selectionData.entryId}-${firstWorkdayIdx}`];
            if (inputRef) {
                inputRef.focus();
            }
        }, 100);
    };
    const handleHourChange = (entryId, dayIndex, value) => {
        if (timesheetStatus !== 'Open' && timesheetStatus !== 'Saved' && timesheetStatus !== 'Rejected') return;
        const numValue = parseFloat(value) || 0;
        setTimeEntries(prev => prev.map(entry => {
            if (entry.id === entryId) {
                return {
                    ...entry,
                    hours: {
                        ...entry.hours,
                        [dayIndex]: numValue
                    }
                };
            }
            return entry;
        }));
    };

    const openCommentModal = (entryId, dayIndex) => {
        setCommentModal({ isOpen: true, entryId, dayIndex });
    };
    const saveComment = (comment) => {
        const key = `${commentModal.entryId}-${commentModal.dayIndex}`;
        setComments(prev => ({ ...prev, [key]: comment }));
        setCommentModal({ isOpen: false, entryId: null, dayIndex: null });
    };
    const getComment = (entryId, dayIndex) => {
        return comments[`${entryId}-${dayIndex}`] || '';
    };
    // Sign timesheet
    const handleSign = () => {
        setShowSignModal(true);
    };
    const confirmSign = () => {
        if (!currentTimesheetId) {
            toast.error("Error: No valid timesheet ID found. Please verify the timesheet is created/saved.");
            console.error("Missing currentTimesheetId", { timesheetStatus });
            return;
        }
        signTimesheet.mutate({
            id: currentTimesheetId,
            orgId
        }, {
            onSuccess: (data) => {
                setTimesheetStatus(data.status || 'Submitted');
                setSignedAt(data.submitted_at || new Date().toISOString());
                setShowSignModal(false);
                toast.success("Timesheet submitted for approval successfully!");
            },
            onError: (err) => {
                toast.error("Failed to sign timesheet: " + (err.message || JSON.stringify(err)));
                console.error("Sign error:", err);
            }
        });
    };

    const calculateEntryTotal = (entry) => {
        if (!entry.hours) return 0;
        return Object.values(entry.hours).reduce((sum, h) => sum + (h || 0), 0).toFixed(1);
    };
    const calculateGrandTotal = () => {
        const entryHours = timeEntries.reduce((sum, entry) => {
            return sum + parseFloat(calculateEntryTotal(entry));
        }, 0);
        return entryHours.toFixed(2);
    };

    const getWarnings = () => {
        const warnings = [];
        timeEntries.forEach(entry => {
            Object.keys(entry.hours || {}).forEach(dayIdx => {
                const date = periodDays[parseInt(dayIdx)];
                if (!date) return;
                const holiday = getPublicHoliday(date);
                const hours = entry.hours[dayIdx];
                if (hours > 8) {
                    warnings.push(`Exceeding 8hrs limit: ${hours}hrs on ${date.toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short' })}`);
                }
                if (holiday && hours > 0 && !entry.isLeave) {
                    warnings.push(`Billing ${hours}hrs on ${holiday.name} (${date.toLocaleDateString()})`);
                }
            });
            if (entry.isLeave && entry.leaveBalance !== null) {
                const totalLeaveHours = Object.values(entry.hours || {}).reduce((s, h) => s + h, 0);
                const leaveDays = totalLeaveHours / 8;
                if (leaveDays > entry.leaveBalance) {
                    warnings.push(`${entry.payType}: Using ${leaveDays} days but only ${entry.leaveBalance} available`);
                }
            }
        });
        return warnings;
    };
    const updateScrollPct = (ref, setPct) => {
        if (!ref.current) return;
        const el = ref.current;
        const pct = (el.scrollLeft / (el.scrollWidth - el.clientWidth)) * 100;
        setPct(isNaN(pct) ? 0 : pct);
    };
    const handleButtonScroll = (ref, direction) => {
        if (ref.current) {
            const amount = 300;
            ref.current.scrollBy({ left: direction === 'left' ? -amount : amount, behavior: 'smooth' });
        }
    };
    const onHandleMouseDown = (e, type) => {
        isDragging.current = true;
        dragTarget.current = type;
        startX.current = e.pageX;
        const ref = type === 'left' ? leftPaneRef : rightPaneRef;
        scrollStart.current = ref.current.scrollLeft;
        document.body.style.userSelect = 'none';
        document.body.style.cursor = 'grabbing';
    };
    useEffect(() => {
        const handleMouseMove = (e) => {
            if (!isDragging.current) return;
            const ref = dragTarget.current === 'left' ? leftPaneRef : rightPaneRef;
            if (!ref.current) return;
            const deltaX = e.pageX - startX.current;
            ref.current.scrollLeft = scrollStart.current + deltaX;
        };
        const handleMouseUp = () => {
            isDragging.current = false;
            document.body.style.cursor = 'default';
        };
        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);
        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, []);
    const handleVerticalScroll = (e) => {
        const { scrollTop } = e.target;
        if (leftPaneRef.current) leftPaneRef.current.scrollTop = scrollTop;
        if (rightPaneRef.current) rightPaneRef.current.scrollTop = scrollTop;
    };
    const getRowStyle = (entry) => {
        if (entry.isLeave) {
            if (entry.chargeType?.id === 'annual_leave') return 'bg-org-blue-primary/5 border-l-4 border-l-org-blue-primary';
            if (entry.chargeType?.id === 'sick_leave') return 'bg-org-green-primary/5 border-l-4 border-l-org-green-primary';
            if (entry.chargeType?.id === 'public_holiday') return 'bg-org-blue-primary/10 border-l-4 border-l-org-blue-primary';
        }
        return '';
    };
    const getStatusColor = () => {
        switch (timesheetStatus) {
            case 'Open': return 'text-org-green-primary bg-org-blue-primary/10';
            case 'Signed': return 'text-org-green-primary bg-org-green-primary/10';
            case 'Submitted': return 'text-org-blue-primary bg-org-blue-primary/10';
            case 'Approved': return 'text-org-green-primary bg-org-green-primary/10';
            case 'Rejected': return 'text-red-600 bg-red-50';
            default: return 'text-gray-600 bg-gray-50';
        }
    };
    // Show tooltip
    const showTooltip = (e, content) => {
        const rect = e.target.getBoundingClientRect();
        setTooltip({
            visible: true,
            x: rect.left + rect.width / 2,
            y: rect.top - 10,
            content
        });
    };
    const hideTooltip = () => {
        setTooltip({ visible: false, x: 0, y: 0, content: '' });
    };
    // Navigate months
    const goToPreviousMonth = () => {
        if (selectedMonth === 0) {
            setSelectedMonth(11);
            setSelectedYear(selectedYear - 1);
        } else {
            setSelectedMonth(selectedMonth - 1);
        }
    };
    const goToNextMonth = () => {
        if (selectedMonth === 11) {
            setSelectedMonth(0);
            setSelectedYear(selectedYear + 1);
        } else {
            setSelectedMonth(selectedMonth + 1);
        }
    };
    const isLocked = timesheetStatus === 'Signed' || timesheetStatus === 'Submitted' || timesheetStatus === 'Approved';

    // future-date blocking
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Submit  only allowed on or after the 22nd of the selected month
    const lastDayOfMonth = new Date(selectedYear, selectedMonth, 22);
    lastDayOfMonth.setHours(0, 0, 0, 0);
    const isMonthComplete = today >= lastDayOfMonth;

    const totalHours = calculateGrandTotal();
    const autoFilledHours = calculateAutoFilledHours();
    const expectedHours = calculateExpectedHours();
    const allHours = parseFloat(totalHours) + autoFilledHours;
    const missingHours = Math.max(0, expectedHours - parseFloat(totalHours)).toFixed(2);
    const warnings = getWarnings();

    const createLineAsync = (entry) => {
        return new Promise((resolve, reject) => {
            createLine.mutate({
                timesheetId: currentTimesheetId,
                orgId,
                data: {
                    project_id: entry.projectCode,
                    activity_code: entry.plc,
                    description: entry.description || ''
                }
            }, {
                onSuccess: (lineData) => {
                    resolve(lineData);
                },
                onError: reject
            });
        });
    };

    const saveEntries = (entries) => {
        return new Promise((resolve, reject) => {
            upsertEntries.mutate({
                timesheetId: currentTimesheetId,
                orgId,
                data: { entries }
            }, {
                onSuccess: resolve,
                onError: reject
            });
        });
    };

    const saveHolidayEntries = (holidaysData) => {
        return new Promise((resolve, reject) => {
            createHolidays.mutate({
                data: holidaysData,
                orgId
            }, {
                onSuccess: resolve,
                onError: reject
            });
        });
    };
    const handleManualSave = async () => {
        if (!currentTimesheetId) {
            toast.error("Error: No timesheet found for this period.");
            console.error("Missing currentTimesheetId", { selectedMonth, selectedYear, timesheetStatus });
            return;
        }
        const year = selectedYear;
        const month = selectedMonth + 1;
        const regularEntries = timeEntries.filter(entry => !entry.isTimeOff);
        const timeOffEntries = timeEntries.filter(entry => entry.isTimeOff && !entry.isHoliday);

        const entriesNeedingLines = regularEntries.filter(
            entry => !entry.lineId && entry.projectCode && entry.plc
        );
        for (const entry of entriesNeedingLines) {
            try {
                const lineData = await createLineAsync(entry);
                entry.lineId = lineData.id;
                setTimeEntries(prev => prev.map(e =>
                    e.id === entry.id ? { ...e, lineId: lineData.id } : e
                ));
            } catch (err) {
                console.error('Error creating line:', err);
                toast.error('Error creating timesheet line: ' + (err.message || 'Unknown error'));
                return;
            }
        }
        // Helper to check if a date is a leave day
        const isLeaveDay = (dateStr) => {
            if (!approvedLeavesData?.leave_dates) return false;
            return approvedLeavesData.leave_dates.includes(dateStr);
        };

        // This ensures removed days are zeroed out on the backend when re-saving after a rejection.
        const allRegularEntries = [];
        regularEntries.forEach(entry => {
            if (!entry.lineId) return;
            // Union of days the user has touched and days that exist in the backend
            const previouslySavedDays = Object.keys(entry._entryIds || {});
            const currentDays = Object.keys(entry.hours || {});
            const allDays = new Set([...currentDays, ...previouslySavedDays]);
            allDays.forEach(dayIdx => {
                const hours = entry.hours?.[dayIdx] || 0;
                const day = parseInt(dayIdx) + 1;
                const workDate = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                const notes = getComment(entry.id, dayIdx);

                const actualHours = isLeaveDay(workDate) ? 0 : parseFloat(hours);
                const entryData = {
                    line_id: entry.lineId,
                    work_date: workDate,
                    hours: actualHours
                };
                if (notes) {
                    entryData.notes = notes;
                }
                allRegularEntries.push(entryData);
            });
        });
 
        const allHolidayEntries = [];
        timeOffEntries.forEach(entry => {
            Object.keys(entry.hours || {}).forEach(dayIdx => {
                const hours = entry.hours[dayIdx];
                if (hours > 0) {
                    const day = parseInt(dayIdx) + 1;
                    const workDate = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                    allHolidayEntries.push({
                        date: workDate,
                        hours: parseFloat(hours),
                        time_off_type: entry.timeOffType || 'personal',
                        name: entry.description || entry.payType || 'Time Off'
                    });
                }
            });
        });
        let savedRegular = false;
        let savedHolidays = false;

        if (allRegularEntries.length > 0) {
            try {
                await saveEntries(allRegularEntries);
                savedRegular = true;
            } catch (err) {
                toast.error("Error saving regular entries: " + (err.message || JSON.stringify(err)));
                console.error("Save error:", err);
                return;
            }
        }
        // Save holiday/time-off entries (bulk)
        if (allHolidayEntries.length > 0) {
            try {
                const holidayPayload = {
                    personal: true,
                    employee_id: employeeId,
                    holidays: allHolidayEntries
                };
                await saveHolidayEntries(holidayPayload);
                savedHolidays = true;
            } catch (err) {
                toast.error("Error saving holiday entries: " + (err.message || JSON.stringify(err)));
                console.error("Holiday save error:", err);
                return;
            }
        }
        if (savedRegular || savedHolidays) {
            toast.success("Timesheet saved successfully!");
        } else {
            toast("No entries to save.");
        }
    };
    const availableYears = getAvailableYears();
    if (isLoadingKind || (isLoadingTimesheet && !monthlyData)) return <PageLoader />;
    if (kindData?.kind === 'labor') return <LaborTimesheet />;
    return (
        <div className="flex flex-col min-h-screen gap-20 font-sans text-text-primary">
            <Breadcrumb title="Timesheet" path="Time & Expense / Time / Timesheets / Timesheet" />
            <div className='grid grid-cols-12 gap-20'>
                {/* 1. TOP INFO CARD */}
                <div className='col-span-12 bg-white rounded border border-border shadow-sm p-20'>
                    <div className='flex flex-col sm:flex-row items-start sm:items-center justify-between mb-20 gap-10'>
                        {/* Month/Year Selector */}
                        <div className='flex items-center gap-10'>
                            <button
                                onClick={goToPreviousMonth}
                                className='p-8 rounded-lg border border-border hover:bg-org-blue-primary/10 hover:border-org-blue-primary transition-colors'
                            >
                                <IconChevronLeft size={18} className='text-org-blue-primary' />
                            </button>
                            <div className='relative' ref={monthPickerRef}>
                                <button
                                    onClick={() => setShowMonthPicker(!showMonthPicker)}
                                    className='flex items-center gap-8 bg-gradient-to-r from-org-blue-primary/10 to-org-green-primary/10 rounded-lg px-12 py-8 border border-org-blue-primary/20 hover:border-org-blue-primary transition-colors'
                                >
                                    <IconCalendarMonth size={16} className='text-org-blue-primary' />
                                    <div className='text-left'>
                                        <p className='text-[9px] text-org-blue-primary font-semibold uppercase tracking-wide'>Selected Period</p>
                                        <p className='text-sm font-bold text-text-primary'>{months[selectedMonth]} {selectedYear}</p>
                                    </div>
                                    <IconChevronDown size={14} className={`text-org-blue-primary transition-transform ${showMonthPicker ? 'rotate-180' : ''}`} />
                                </button>
                                {/* Month/Year Picker Dropdown */}
                                {showMonthPicker && (
                                    <div className='dropdown-enter absolute top-full left-0 mt-4 bg-bg-dark border border-border rounded-lg shadow-lg z-50 p-12 min-w-[220px]'>
                                        {/* Year Selection */}
                                        <div className='mb-10'>
                                            <p className='text-[10px] text-text-secondary font-semibold uppercase mb-6'>Year</p>
                                            <div className='flex gap-6'>
                                                {availableYears.map(year => (
                                                    <button
                                                        key={year}
                                                        onClick={() => setSelectedYear(year)}
                                                        className={`flex-1 py-5 rounded text-xs font-semibold transition-colors ${selectedYear === year
                                                            ? 'bg-org-blue-primary text-white'
                                                            : 'bg-bg-light text-text-secondary hover:bg-org-blue-primary/10'
                                                            }`}
                                                    >
                                                        {year}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                        {/* Month Selection */}
                                        <div>
                                            <p className='text-[10px] text-text-secondary font-semibold uppercase mb-6'>Month</p>
                                            <div className='grid grid-cols-3 gap-5'>
                                                {months.map((month, idx) => (
                                                    <button
                                                        key={month}
                                                        onClick={() => {
                                                            setSelectedMonth(idx);
                                                            setShowMonthPicker(false);
                                                        }}
                                                        className={`py-5 px-6 rounded text-[11px] font-semibold transition-colors ${selectedMonth === idx
                                                            ? 'bg-org-green-primary text-white'
                                                            : 'bg-bg-light text-text-secondary hover:bg-org-green-primary/10'
                                                            }`}
                                                    >
                                                        {month.slice(0, 3)}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                            <button
                                onClick={goToNextMonth}
                                className='p-8 rounded-lg border border-border hover:bg-org-blue-primary/10 hover:border-org-blue-primary transition-colors'
                            >
                                <IconChevronRight size={18} className='text-org-blue-primary' />
                            </button>
                        </div>
                        <div className='flex items-center gap-6 flex-wrap'>
                            <ReportDownloadButton
                                label='Download Report'
                                orgId={orgId}
                                canPickEmployee={isReportHR}
                                employees={reportEmployees || []}
                                currentEmployeeId={employeeId}
                                selfOptionLabel='My Timesheet'
                                isPending={downloadStandardReport.isPending}
                                onDownload={(params) => downloadStandardReport.mutate(params)}
                            />
                            <button
                                disabled={isLocked}
                                onClick={handleManualSave}
                                className='flex items-center gap-2 bg-gray-100 text-gray-600 border border-gray-200 px-6 py-3 rounded text-[11px] font-medium hover:bg-gray-200 disabled:opacity-50'
                            >
                                <IconDeviceFloppy size={12} />
                                {timesheetStatus === 'New' ? 'Create Timesheet' : 'Save'}
                            </button>
                            <button
                                onClick={handleSign}
                                disabled={(timesheetStatus !== 'Open' && timesheetStatus !== 'Rejected') || !isMonthComplete}
                                title={!isMonthComplete ? `Available from ${lastDayOfMonth.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}` : ''}
                                className='flex items-center gap-2 bg-primary text-white px-6 py-3 rounded text-[11px] font-medium hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed'
                            >
                                <IconSignature size={14} />Sign
                            </button>
                        </div>
                    </div>
                    <div className='pt-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-10'>
                        <div className='flex flex-col sm:flex-row items-start sm:items-center gap-10 sm:gap-40'>
                            <h2 className='text-lg font-bold text-gray-800 sm:pr-20 sm:border-r border-border'>Monthly Timesheet</h2>
                            <div className='flex flex-wrap gap-15 sm:gap-40 text-xs'>
                                <div><p className='text-gray-500 mb-2 uppercase text-[9px] font-bold tracking-wider'>Name</p><p className='font-semibold text-sm whitespace-nowrap'>{user?.first_name ? `${user.first_name} ${user.last_name}` : user?.username || user?.email || 'User'}</p></div>
                                <div><p className='text-gray-500 mb-2 uppercase text-[9px] font-bold tracking-wider'>Employee No</p><p className='font-semibold text-sm'>{myProfile?.employee_no || monthlyData?.employee?.employee_no || 'N/A'}</p></div>
                                <div>
                                    <p className='text-gray-500 mb-2 uppercase text-[9px] font-bold tracking-wider'>Status</p>
                                    <span className={`font-bold text-sm uppercase px-8 py-2 rounded ${getStatusColor()}`}>
                                        {isLocked && <IconLock size={12} className="inline mr-4" />}
                                        {timesheetStatus}
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div className='bg-gradient-to-r from-org-blue-primary/5 to-org-green-primary/5 px-15 py-10 rounded border border-org-blue-primary/20 hidden sm:flex items-center gap-12'>
                            <IconCalendarMonth size={20} className='text-org-blue-primary' />
                            <div>
                                <p className='text-gray-500 uppercase text-[9px] font-bold tracking-wider'>Period</p>
                                <p className='font-bold text-sm text-gray-800'>{months[selectedMonth]} {selectedYear}</p>
                            </div>
                        </div>
                    </div>
                    {/* Signed/Submitted info */}
                    {signedAt && (
                        <div className='mt-15 pt-15 border-t border-border flex items-center gap-20 text-xs'>
                            <div className='flex items-center gap-6 text-org-green-primary'>
                                <IconSignature size={14} />
                                <span>Signed: {new Date(signedAt).toLocaleString()}</span>
                            </div>
                            {submittedAt && (
                                <div className='flex items-center gap-6 text-org-blue-primary'>
                                    <IconCheck size={14} />
                                    <span>Submitted: {new Date(submittedAt).toLocaleString()}</span>
                                </div>
                            )}
                        </div>
                    )}
                    {/* Supervisor rejection comment */}
                    {timesheetStatus === 'Rejected' && supervisorComment && (
                        <div className='mt-15 p-10 bg-red-50 border border-red-200 rounded text-sm text-red-700'>
                            <strong>Supervisor Comment:</strong> {supervisorComment}
                        </div>
                    )}
                </div>
                {/* Warnings */}
                {warnings.length > 0 && (
                    <div className='col-span-12 bg-orange-50 border border-orange-200 rounded p-15'>
                        <div className='flex items-center gap-8 text-orange-700 font-semibold text-sm mb-8'>
                            <IconAlertTriangle size={18} />
                            Validation Warnings
                        </div>
                        <ul className='text-xs text-orange-600 space-y-4'>
                            {warnings.map((w, i) => (
                                <li key={i} className='flex items-center gap-6'>
                                    <span className='w-4 h-4 rounded-full bg-orange-400'></span>
                                    {w}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
                {/* 2. SUMMARY CARDS - Blue and Green colors */}
                <div className="col-span-12 grid grid-cols-2 md:grid-cols-4 gap-20">
                    <div className="bg-white p-15 rounded border border-border shadow-sm flex items-center gap-15">
                        <div className="w-40 h-40 rounded bg-org-blue-primary/10 flex items-center justify-center text-org-blue-primary font-bold"><IconClock size={20} /></div>
                        <div><p className="text-[10px] uppercase text-gray-500 font-bold tracking-tight">Total Hours</p><p className="text-lg font-bold text-org-blue-primary">{totalHours}</p></div>
                    </div>
                    <div className="bg-white p-15 rounded border border-border shadow-sm flex items-center gap-15">
                        <div className="w-40 h-40 rounded bg-org-green-primary/10 flex items-center justify-center text-org-green-primary font-bold"><IconBeach size={20} /></div>
                        <div><p className="text-[10px] uppercase text-gray-500 font-bold tracking-tight">Holiday/Leave (Auto)</p><p className="text-lg font-bold text-org-green-primary">{autoFilledHours}h</p></div>
                    </div>
                    <div className="bg-white p-15 rounded border border-border shadow-sm flex items-center gap-15">
                        <div className={`w-40 h-40 rounded flex items-center justify-center font-bold ${parseFloat(missingHours) > 0 ? 'bg-org-blue-primary/10 text-org-blue-primary' : 'bg-org-green-primary/10 text-org-green-primary'}`}>
                            {parseFloat(missingHours) > 0 ? <IconAlertCircle size={20} /> : <IconCheck size={20} />}
                        </div>
                        <div>
                            <p className="text-[10px] uppercase text-gray-500 font-bold tracking-tight">
                                {parseFloat(missingHours) > 0 ? 'Differences' : 'Complete'}
                            </p>
                            <p className={`text-lg font-bold ${parseFloat(missingHours) > 0 ? 'text-org-blue-primary' : 'text-org-green-primary'}`}>
                                {parseFloat(missingHours) > 0 ? missingHours : totalHours}
                            </p>
                        </div>
                    </div>
                    <div className="bg-white p-15 rounded border border-border shadow-sm flex items-center gap-15">
                        <div className="w-40 h-40 rounded bg-org-blue-primary/10 flex items-center justify-center text-org-blue-primary font-bold"><IconCalendarCheck size={20} /></div>
                        <div><p className="text-[10px] uppercase text-gray-500 font-bold tracking-tight">Expected Work Hours</p><p className="text-lg font-bold">{expectedHours}</p></div>
                    </div>
                </div>
                {/* 3. TABLE CARD - Blue header */}
                <div className='col-span-12 bg-white p-20 rounded border border-border shadow-sm flex flex-col'>
                    <div className='flex items-center justify-between mb-15'>
                        <h3 className='text-sm font-semibold text-gray-700'>Timesheet Lines</h3>
                        <div className='flex items-center gap-10'>
                            {selectedIds.length > 0 && !isLocked && (
                                <button onClick={handleDeleteSelected} className='bg-red-50 text-red-600 border border-red-200 px-10 py-5 rounded text-xs flex items-center gap-4 hover:bg-red-100 font-semibold'><IconTrash size={14} />Delete Selected</button>
                            )}
                            <button
                                onClick={handleAddNewLine}
                                disabled={isLocked}
                                className='bg-org-green-primary text-white px-10 py-5 rounded text-xs flex items-center gap-4 hover:opacity-90 font-semibold disabled:opacity-50'
                            >
                                <IconPlus size={14} />New Line
                            </button>
                        </div>
                    </div>
                    <p className="text-xs text-gray-400 sm:hidden mb-8 flex items-center gap-4">
                        <IconChevronLeft size={10} /><IconChevronRight size={10} /> Scroll left/right to view all dates
                    </p>
                    <div className="flex border border-gray-300 rounded overflow-hidden">
                        {/* LEFT SECTION */}
                        <div className="w-[42%] sm:w-[38%] border-r-2 border-gray-400 flex flex-col">
                            <div className="overflow-x-auto overflow-y-auto scrollbar-hide max-h-[450px]" ref={leftPaneRef} onScroll={(e) => { updateScrollPct(leftPaneRef, setLeftScrollPct); handleVerticalScroll(e); }}>
                                <div className="min-w-[470px] flex flex-col">
                                    {/* Blue header */}
                                    <div className="h-[56px] bg-org-blue-primary text-white flex items-center text-[10px] font-bold uppercase sticky top-0 z-[2]">
                                        <div className="w-[30px] px-4 border-r border-white/20 text-center">
                                            <input
                                                type="checkbox"
                                                checked={selectedIds.length === timeEntries.length && timeEntries.length > 0}
                                                onChange={handleSelectAll}
                                                disabled={isLocked}
                                            />
                                        </div>
                                        <div className="w-[30px] px-2 border-r border-white/20 text-center">#</div>
                                        <div className="w-[150px] px-8 border-r border-white/20">Description</div>
                                        <div className="w-[100px] px-6 border-r border-white/20">Code</div>
                                        <div className="w-[80px] px-6 border-r border-white/20">PLC</div>
                                        <div className="flex-grow px-6">Pay Type</div>
                                    </div>
                                    {timeEntries.map((entry, idx) => (
                                        <div key={entry.id} className={`h-[48px] border-b border-border flex items-center text-sm ${getRowStyle(entry)}`}>
                                            <div className="w-[30px] px-4 border-r h-full flex items-center justify-center">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedIds.includes(entry.id)}
                                                    onChange={() => handleCheckboxChange(entry.id)}
                                                    disabled={isLocked}
                                                />
                                            </div>
                                            <div className="w-[30px] px-2 border-r h-full flex items-center justify-center bg-gray-50 text-gray-400 font-bold text-[11px]">{idx + 1}</div>
                                            <div className="w-[150px] px-8 border-r h-full flex items-center relative group">
                                                <div className="flex-grow overflow-hidden">
                                                    {entry.description ? (
                                                        <div className="flex items-center gap-4">
                                                            <span className="text-xs font-medium truncate">{entry.description}</span>
                                                            {entry.isLeave && entry.leaveBalance !== null && (
                                                                <span className="flex items-center gap-2 px-4 py-1 bg-org-green-primary/10 text-org-green-primary text-[8px] font-bold rounded-full whitespace-nowrap">
                                                                    <IconBeach size={8} />
                                                                    {entry.leaveBalance}d
                                                                </span>
                                                            )}
                                                        </div>
                                                    ) : (
                                                        <span className="text-xs text-gray-400 italic">Select...</span>
                                                    )}
                                                </div>
                                                {!isLocked && (
                                                    <IconSearch
                                                        size={12}
                                                        className="text-gray-300 absolute right-6 cursor-pointer hover:text-org-blue-primary flex-shrink-0"
                                                        onClick={() => openLookupForEntry(entry.id)}
                                                    />
                                                )}
                                            </div>
                                            <div className="w-[100px] px-6 border-r h-full flex items-center">
                                                <span className={`text-[10px] ${entry.projectCode ? 'font-mono bg-gray-100 px-4 py-1 rounded truncate' : 'text-gray-400 italic'}`}>
                                                    {entry.projectCode || '-'}
                                                </span>
                                            </div>
                                            <div className="w-[80px] px-6 border-r h-full flex items-center">
                                                <span className={`text-[10px] truncate ${entry.plc ? '' : 'text-gray-400 italic'}`}>
                                                    {entry.plc || '-'}
                                                </span>
                                            </div>
                                            <div className="flex-grow px-6 h-full flex items-center">
                                                <span className={`text-[10px] ${entry.payType ? 'font-medium' : 'text-gray-400 italic'}`}>
                                                    {entry.payType || '-'}
                                                </span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            {/* CUSTOM SCROLLER LEFT */}
                            <div className="h-[24px] bg-gray-100 border-t border-border flex items-center relative z-[2]">
                                <button onClick={() => handleButtonScroll(leftPaneRef, 'left')} className="h-full px-2 border-r border-border hover:bg-gray-200"><IconChevronLeft size={12} /></button>
                                <div className="flex-grow h-full relative mx-1">
                                    <div onMouseDown={(e) => onHandleMouseDown(e, 'left')} className="absolute top-1/2 -translate-y-1/2 h-[8px] bg-org-blue-primary rounded-full cursor-grab active:cursor-grabbing hover:opacity-80" style={{ width: '30%', left: `${leftScrollPct * 0.7}%` }}></div>
                                </div>
                                <button onClick={() => handleButtonScroll(leftPaneRef, 'right')} className="h-full px-2 border-l border-border hover:bg-gray-200"><IconChevronRight size={12} /></button>
                            </div>
                        </div>
                        {/* RIGHT SECTION - Date columns */}
                        <div className="flex-grow flex flex-col overflow-hidden">
                            <div className="overflow-x-auto overflow-y-auto scrollbar-hide max-h-[450px]" ref={rightPaneRef} onScroll={(e) => { updateScrollPct(rightPaneRef, setRightScrollPct); handleVerticalScroll(e); }}>
                                <div className="flex">
                                    {periodDays.map((date, dayIdx) => {
                                        const weekend = isWeekend(date);
                                        const holiday = getPublicHoliday(date);
                                        const leave = getApprovedLeave(date);
                                        const blocked = weekend || holiday || leave;
                                        // From the 22nd onwards, all remaining days of the month become fillable
                                        const isFutureDate = isMonthComplete ? false : date > today;
                                        // Blue for headers, green accents for holidays/leave
                                        const getColumnBg = () => {
                                            if (isFutureDate) return 'bg-gray-50/80';
                                            if (holiday) return 'bg-org-green-primary/5';
                                            if (leave) return 'bg-org-blue-primary/5';
                                            if (weekend) return 'bg-gray-50';
                                            return '';
                                        };
                                        const getHeaderBgColor = () => {
                                            if (holiday) return '#6CBC50'; // org-green-primary (same as sign button)
                                            if (leave) return '#dc2626'; // red-600
                                            if (weekend) return '#9ca3af'; // gray
                                            return '#012E81'; // org-blue-primary
                                        };
                                        return (
                                            <div key={dayIdx} className={`min-w-[70px] sm:min-w-[85px] border-r border-border ${getColumnBg()}`}>
                                                {/* Header */}
                                                <div
                                                    className="h-[56px] text-white flex flex-col items-center justify-center text-[10px] font-medium sticky top-0 z-[2] border-r border-white/20 relative overflow-hidden cursor-default"
                                                    style={{ backgroundColor: getHeaderBgColor() }}
                                                >
                                                    <span className="font-bold">{date.toLocaleDateString('en-GB', { weekday: 'short' })}</span>
                                                    <span className="opacity-80 text-[9px]">{date.getDate()}</span>
                                                    {/* Holiday/Leave badge */}
                                                    {(holiday || leave) && !weekend && (
                                                        <div className="absolute bottom-1 left-1/2 -translate-x-1/2">
                                                            <span className="bg-white/30 backdrop-blur-sm px-2 py-0.5 rounded text-[6px] font-bold uppercase tracking-wide whitespace-nowrap">
                                                                {holiday ? 'Holiday' : 'Leave'}
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>
                                                {/* Entry rows */}
                                                {timeEntries.map((entry) => {
                                                    const hasComment = getComment(entry.id, dayIdx);
                                                    const hoursValue = entry.hours?.[dayIdx] || 0;
                                                    const exceedsMax = hoursValue > 8;

                                                    // ────────────────────────────────────────────────
                                                    //  STRICT AUTO-FILL CONDITIONS - THIS IS THE FIX
                                                    // ────────────────────────────────────────────────
                                                    const hasProject = !!entry.projectCode && entry.projectCode !== '';
                                                    const isRealProjectLine = hasProject && !entry.isLeave && !entry.isTimeOff && !entry.isHoliday;
                                                    const isRealTimeOffLine = entry.isTimeOff || entry.isLeave || entry.isHoliday;

                                                    const shouldAutoFillLeave = leave && !weekend && isRealProjectLine;
                                                    const shouldAutoFillHoliday = holiday && !weekend && isRealProjectLine;

                                                    const isAutoFilled = shouldAutoFillLeave || shouldAutoFillHoliday;

                                                    // ────────────────────────────────────────────────
                                                    //  DISABLE INPUT LOGIC
                                                    // ────────────────────────────────────────────────
                                                    const inputDisabled =
                                                        isLocked ||
                                                        weekend ||
                                                        isFutureDate ||
                                                        (leave && isRealProjectLine) ||
                                                        (holiday && isRealProjectLine) ||
                                                        (!hasProject && !isRealTimeOffLine);

                                                    // ────────────────────────────────────────────────
                                                    //  CELL BACKGROUND
                                                    // ────────────────────────────────────────────────
                                                    const getCellBg = () => {
                                                        if (exceedsMax) return 'bg-red-100 border-2 border-red-400';
                                                        if (shouldAutoFillHoliday) return 'bg-green-50';
                                                        if (shouldAutoFillLeave) return 'bg-red-50';
                                                        if (weekend) return 'bg-gray-100';
                                                        if (!hasProject && !inputDisabled) return 'bg-yellow-50/40'; // hint for empty new lines
                                                        return 'bg-white';
                                                    };

                                                    return (
                                                        <div
                                                            key={entry.id}
                                                            className={`h-[48px] border-b border-border flex items-center justify-center relative group ${getCellBg()}`}
                                                            onMouseEnter={(e) => {
                                                                if (shouldAutoFillLeave) {
                                                                    showTooltip(e, `${leave.leaveTypeName || 'Approved Leave'} — 8h auto-filled`);
                                                                } else if (shouldAutoFillHoliday) {
                                                                    showTooltip(e, `Public Holiday: ${holiday?.name || 'Holiday'} — 8h auto-filled`);
                                                                } else if (exceedsMax) {
                                                                    showTooltip(e, `Warning: Exceeds daily 8hr limit`);
                                                                }
                                                            }}
                                                            onMouseLeave={hideTooltip}
                                                        >
                                                            {isAutoFilled ? (
                                                                <div className="relative flex items-center justify-center w-full h-full">
                                                                    <div className="absolute inset-0 backdrop-blur-[1px]"></div>
                                                                    <span className="relative z-10 text-sm font-bold text-gray-900">
                                                                        8.0
                                                                    </span>
                                                                    <IconLock
                                                                        size={10}
                                                                        className={`absolute top-1 right-1 ${shouldAutoFillHoliday ? 'text-green-600/60' : 'text-red-600/60'
                                                                            }`}
                                                                    />
                                                                </div>
                                                            ) : (
                                                                <>
                                                                    {/* Warning icon for exceeding hours */}
                                                                    {exceedsMax && (
                                                                        <div className="absolute top-1 left-1 z-20">
                                                                            <IconAlertTriangle size={12} className="text-red-500" />
                                                                        </div>
                                                                    )}
                                                                    <input
                                                                        ref={el => dateInputRefs.current[`${entry.id}-${dayIdx}`] = el}
                                                                        type="number"
                                                                        step="0.5"
                                                                        min="0"
                                                                        disabled={inputDisabled}
                                                                        value={entry.hours?.[dayIdx] || ''}
                                                                        onChange={(e) => handleHourChange(entry.id, dayIdx, e.target.value)}
                                                                        className={`w-full text-center text-sm outline-none bg-transparent relative z-10
                                                                            ${inputDisabled ? 'cursor-not-allowed opacity-40' : ''}
                                                                            ${exceedsMax ? 'text-red-600 font-bold' : ''}
                                                                            focus:bg-org-blue-primary/10 focus:font-semibold
                                                                        `}
                                                                        placeholder={inputDisabled ? "" : "0.0"}
                                                                    />
                                                                    {/* Comment indicator */}
                                                                    {hasComment && (
                                                                        <div className="absolute top-1 right-1 w-2 h-2 bg-org-blue-primary rounded-full z-20" title={hasComment}></div>
                                                                    )}
                                                                    {/* Comment button on hover */}
                                                                    {!weekend && hasProject && !isLocked && entry.hours?.[dayIdx] > 0 && (
                                                                        <button
                                                                            onClick={() => openCommentModal(entry.id, dayIdx)}
                                                                            className="absolute bottom-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity z-20"
                                                                        >
                                                                            <IconMessage size={12} className="text-gray-400 hover:text-org-blue-primary" />
                                                                        </button>
                                                                    )}
                                                                </>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        );
                                    })}
                                    {/* Total column */}
                                    <div className="min-w-[100px] sticky right-0 z-[2] bg-gray-50 border-l border-border shadow-[-2px_0_5px_rgba(0,0,0,0.05)]">
                                        <div className="h-[56px] bg-org-blue-primary text-white flex items-center justify-center text-[11px] font-bold uppercase sticky top-0">Total</div>
                                        {timeEntries.map((entry) => (
                                            <div key={entry.id} className={`h-[48px] border-b border-border flex items-center justify-center font-bold text-sm bg-gray-100 text-gray-800`}>
                                                {calculateEntryTotal(entry)}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                            {/* CUSTOM SCROLLER RIGHT */}
                            <div className="h-[24px] bg-gray-100 border-t border-border flex items-center relative z-[2]">
                                <button onClick={() => handleButtonScroll(rightPaneRef, 'left')} className="h-full px-2 border-r border-border hover:bg-gray-200"><IconChevronLeft size={12} /></button>
                                <div className="flex-grow h-full relative mx-1">
                                    <div onMouseDown={(e) => onHandleMouseDown(e, 'right')} className="absolute top-1/2 -translate-y-1/2 h-[8px] bg-org-blue-primary rounded-full cursor-grab active:cursor-grabbing hover:opacity-80" style={{ width: '20%', left: `${rightScrollPct * 0.8}%` }}></div>
                                </div>
                                <button onClick={() => handleButtonScroll(rightPaneRef, 'right')} className="h-full px-2 border-l border-border hover:bg-gray-200"><IconChevronRight size={12} /></button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {/* Global Tooltip */}
            {tooltip.visible && (
                <div
                    className="fixed z-[200] px-10 py-6 bg-gray-900 text-white text-xs rounded shadow-lg pointer-events-none transform -translate-x-1/2 -translate-y-full"
                    style={{ left: tooltip.x, top: tooltip.y }}
                >
                    {tooltip.content}
                    <div className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                </div>
            )}
            <ChargeLookupModal
                isOpen={isLookupOpen}
                onClose={() => {
                    setIsLookupOpen(false);
                    setActiveEntryId(null);
                }}
                onSelect={handleLookupSelect}
                entryId={activeEntryId}
                projects={projects}
                organizations={organizations}
            />
            {/* Comment Modal */}
            {commentModal.isOpen && (
                <>
                    <div className="fixed inset-0 z-[100] flex items-center justify-center p-20">
                        <div className="w-full max-w-[400px] bg-white rounded-xl shadow-2xl overflow-hidden">
                            <div className="bg-primary px-20 py-15 flex items-center justify-between">
                                <h3 className="text-lg font-semibold text-white flex items-center gap-8">
                                    <IconMessage size={20} />
                                    Activity Comment
                                </h3>
                                <button onClick={() => setCommentModal({ isOpen: false, entryId: null, dayIndex: null })} className="p-4 hover:bg-white/20 rounded-full">
                                    <IconX size={20} className="text-white" />
                                </button>
                            </div>
                            <div className="p-20">
                                <label className="text-xs text-gray-500 mb-8 block font-semibold uppercase">Job Done / Notes</label>
                                <textarea
                                    className="w-full border border-border rounded-lg p-12 text-sm resize-none focus:outline-none focus:border-org-blue-primary"
                                    rows={4}
                                    placeholder="Describe the work completed..."
                                    defaultValue={getComment(commentModal.entryId, commentModal.dayIndex)}
                                    id="commentInput"
                                />
                            </div>
                            <div className="px-20 py-15 border-t border-border flex justify-end gap-10">
                                <button onClick={() => setCommentModal({ isOpen: false, entryId: null, dayIndex: null })} className="btn btn-outline-secondary">Cancel</button>
                                <button
                                    onClick={() => saveComment(document.getElementById('commentInput').value)}
                                    className="btn btn-primary"
                                >
                                    Save Comment
                                </button>
                            </div>
                        </div>
                    </div>
                    <div onClick={() => setCommentModal({ isOpen: false, entryId: null, dayIndex: null })} className="fixed inset-0 z-[99] bg-black/50 backdrop-blur-sm"></div>
                </>
            )}
            {/* Sign Modal */}
            {showSignModal && (
                <>
                    <div className="fixed inset-0 z-[100] flex items-center justify-center p-20">
                        <div className="w-full max-w-[450px] bg-white rounded-xl shadow-2xl overflow-hidden">
                            <div className="bg-primary px-20 py-15 flex items-center justify-between">
                                <h3 className="text-lg font-semibold text-white flex items-center gap-8">
                                    <IconSignature size={20} />
                                    Electronic Signature
                                </h3>
                                <button onClick={() => setShowSignModal(false)} className="p-4 hover:bg-white/20 rounded-full">
                                    <IconX size={20} className="text-white" />
                                </button>
                            </div>
                            <div className="p-20">
                                <div className="bg-gray-50 border border-border rounded-lg p-15 mb-20">
                                    <p className="text-sm text-gray-600 mb-10">By signing this timesheet, I certify that:</p>
                                    <ul className="text-xs text-gray-500 space-y-6">
                                        <li className="flex items-start gap-8">
                                            <IconCheck size={14} className="text-primary mt-1 flex-shrink-0" />
                                            All hours reported are accurate and complete
                                        </li>
                                        <li className="flex items-start gap-8">
                                            <IconCheck size={14} className="text-primary mt-1 flex-shrink-0" />
                                            Time has been charged to the correct projects
                                        </li>
                                        <li className="flex items-start gap-8">
                                            <IconCheck size={14} className="text-primary mt-1 flex-shrink-0" />
                                            I understand this timesheet will be locked after signing
                                        </li>
                                    </ul>
                                </div>
                                <div className="flex items-center gap-15 p-15 bg-primary/10 rounded-lg">
                                    <div className="w-50 h-50 rounded-full bg-primary/20 flex items-center justify-center">
                                        <IconSignature size={24} className="text-primary" />
                                    </div>
                                    <div>
                                        <p className="font-semibold text-gray-800">{user?.first_name ? `${user.first_name} ${user.last_name}` : user?.username || user?.email || 'User'}</p>
                                        <p className="text-xs text-gray-500">{new Date().toLocaleString()}</p>
                                    </div>
                                </div>
                            </div>
                            <div className="px-20 py-15 border-t border-border flex justify-end gap-10">
                                <button onClick={() => setShowSignModal(false)} className="btn btn-outline-secondary" disabled={signTimesheet.isPending}>Cancel</button>
                                <button onClick={confirmSign} disabled={signTimesheet.isPending} className="btn btn-primary disabled:opacity-60 disabled:cursor-not-allowed">
                                    {signTimesheet.isPending ? 'Signing...' : 'Confirm & Sign'}
                                </button>
                            </div>
                        </div>
                    </div>
                    <div onClick={() => setShowSignModal(false)} className="fixed inset-0 z-[99] bg-black/50 backdrop-blur-sm"></div>
                </>
            )}
        </div>
    )
}